/**
 * FN-FE-02 — App Layout — PROTOTÍPUS SZERINTI: Topbar + Workspace + FKeyBar (no sidebar)
 */
import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Topbar from './Topbar';
import FKeyBar from './FKeyBar';
import { FKeyBarProvider } from './FKeyBar';
import { ToastProvider } from '../ui/Toast';
import AppUpdatePrompt from '../AppUpdatePrompt';
import PwaInstallPrompt from '../PwaInstallPrompt';
import ImpersonationBanner from '../ImpersonationBanner';
import CrispChat from '../CrispChat';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureContext } from '../../contexts/FeatureContext';
import TrialBanner from '../subscription/TrialBanner';
import LockedScreen from '../subscription/LockedScreen';
import PaymentRequiredModal from '../subscription/PaymentRequiredModal';
import { post } from '../../lib/api';

export default function AppLayout() {
  const { impersonating } = useAuth();

  // Heartbeat: naponta egyszer jelzi a szervernek hogy az app nyitva van
  // (reggeli push csak azoknak megy ki, akik még nem nyitottak be aznap)
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem('modulum_heartbeat') === today) return;
    post('/push/heartbeat', {}).then(() => {
      localStorage.setItem('modulum_heartbeat', today);
    }).catch(() => {});
  }, []);
  const {
    status,
    trialStartedAt,
    trialEndsAt,
    discount15Offered,
    discount15Applied,
    warning28Shown,
    dataPurgeAt,
    loading: featuresLoading,
    refresh,
  } = useFeatureContext();
  const location = useLocation();
  const [verifyingPayment, setVerifyingPayment] = useState(() => {
    // Ha ?payment=done van az URL-ben, azonnal elnyomjuk a modalt
    const params = new URLSearchParams(window.location.search);
    return params.get('payment') === 'done';
  });

  // Barion visszairányítás utáni fizetés ellenőrzés
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get('payment');
    const paymentId = params.get('paymentId');
    const subId = params.get('subId');
    if (payment === 'done' && paymentId && subId) {
      const clean = location.pathname;
      window.history.replaceState({}, '', clean);
      post<{ success: boolean }>('/subscriptions/verify-payment', { paymentId, subId })
        .then(r => {
          if (r.success) {
            window.location.replace(clean);
          } else {
            setVerifyingPayment(false);
            refresh();
          }
        })
        .catch(() => { setVerifyingPayment(false); refresh(); });
    }
  }, []);

  // Barion fizetés ellenőrzés alatt: töltő képernyő
  if (verifyingPayment) {
    return (
      <div style={{
        position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: '#fff', zIndex: 9999, gap: 20,
      }}>
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
          <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3"/>
          <path d="M12 2a10 10 0 0 1 10 10" stroke="#14233F" strokeWidth="3" strokeLinecap="round"/>
        </svg>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#14233F' }}>Fizetés ellenőrzése…</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Locked: teljes adatmegsemmisítés előtt zár képernyő
  if (!featuresLoading && status === 'locked') {
    const daysUntilPurge = dataPurgeAt
      ? Math.max(0, Math.ceil((new Date(dataPurgeAt).getTime() - Date.now()) / 86400000))
      : 30;
    return (
      <ToastProvider>
        <LockedScreen daysUntilPurge={daysUntilPurge} />
      </ToastProvider>
    );
  }

  const showTrialBanner = !!status && ['trial', 'past_due'].includes(status);
  const showPaymentModal = !verifyingPayment && !!status && ['grace', 'locked'].includes(status);

  return (
    <ToastProvider>
      <FKeyBarProvider>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          {impersonating && <ImpersonationBanner impersonating={impersonating} />}
          <Topbar onMenuClick={() => {}} />
          {showTrialBanner && (
            <TrialBanner
              status={status}
              trialStartedAt={trialStartedAt}
              trialEndsAt={trialEndsAt}
              discount15Offered={discount15Offered}
              discount15Applied={discount15Applied}
              warning28Shown={warning28Shown}
              dataPurgeAt={dataPurgeAt}
            />
          )}
          {showPaymentModal && <PaymentRequiredModal />}
          <main className="workspace" style={impersonating ? { paddingTop: 40 } : undefined}>
            <div className="wrap">
              <Outlet />
            </div>
          </main>
          <FKeyBarDisplay />
          <AppUpdatePrompt />
          <PwaInstallPrompt />
          <CrispChat />
        </div>
      </FKeyBarProvider>
    </ToastProvider>
  );
}

function FKeyBarDisplay() {
  return <FKeyBar />;
}
